import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

// material-ui
import { useTheme, styled } from '@mui/material/styles';
import { AppBar as MuiAppBar, Container, Stack, Toolbar, Typography, useScrollTrigger, Box, Avatar, IconButton } from '@mui/material';

import { useDispatch, useSelector } from 'store';
import { setPreviousRoute } from 'store/slices/account';
import MerLionHeader from 'components/MerLionHeader';
import SearchBar from 'components/searchbar';
import Logo from 'assets/images/imda-full-logo.svg';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { LOGIN_TEXT, APP_TEXT, DB_TEXT, LOGOUT_TEXT, GUIDES_TEXT } from 'common/constants';
import ImdaButton from 'components/button';
import MobileMenu from './MobileMenu';
import NotificationList from './Notification';
import styles from './appbar.module.css';
import { useLogout } from 'hooks/useLogout';
import { axiosServicesV3 } from 'utils/services';
import ScreenLoader from 'features/applicationPage/components/ScreenLoader';
import { decryptId } from 'common/decrypt';

const StyledLink = styled(NavLink)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '28px',
    textDecoration: 'none',
    padding: '0.5rem 0.6rem',
    '&:hover': {
        color: theme.palette.primary.dark,
        backgroundColor: 'rgba(111, 54, 142, 0.2)',
        borderRadius: '60px'
    }
}));

// elevation scroll
interface ElevationScrollProps {
    children: ReactElement;
    window?: Window | Node;
}

function ElevationScroll({ children, window }: ElevationScrollProps) {
    const theme = useTheme();
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 0,
        target: window!
    });
    const darkBorder = theme.palette.mode === 'dark' ? theme.palette.dark.dark : theme.palette.grey[200];
    const borderBottom = trigger ? 'none' : '1px solid';
    const borderColor = trigger ? '' : darkBorder;

    return React.cloneElement(children, {
        elevation: trigger ? 2 : 0,
        style: {
            backgroundColor: theme.palette.background.default,
            borderBottom,
            borderColor,
            color: theme.palette.text.dark
        }
    });
}

// ==============================|| MINIMAL LAYOUT APP BAR ||============================== //

function AppBar({ Header, isVisible }: { Header?: React.ReactNode; isVisible?: boolean }) {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [isLoggingOut, setLogout] = useState(false);
    const dispatch = useDispatch();
    const { isLoggedIn, user } = useSelector((state) => state.account);
    const { cases } = useSelector((state) => state.dashboard);
    const renderRef = useRef(false);
    const logout = useLogout();
    const [userId, setUserId] = useState('');
    const [userUen, setUserUen] = useState('');

    const fetchDecryptedUserDetails = async () => {
        const decryptedName = await decryptId(user?.name || '', 'decrypt');
        setUserId(cases.length > 0 ? decryptedName : '');
        setUserUen(cases.length > 0 ? decryptedName : '');
    };

    useEffect(() => {
        renderRef.current = true;
        document.cookie = 'iris=true; Path=/;';
        if (isLoggedIn) {
            fetchDecryptedUserDetails();
        }
    }, []);

    const handleLogin = async () => {
        // setUser(!user);
        if (!isLoggedIn) {
            dispatch(setPreviousRoute(null));
            navigate('/login');
        } else {
            setLogout(true);
            const decrypted = await decryptId(user?.id || '', 'decrypt');

            const response = await axiosServicesV3.post('logout/', {
                ID: decrypted
            });
            if (response.status === 200) {
                setLogout(false);
                logout();
            } else {
                setLogout(false);
            }
        }
    };

    const handleOnclickSearch = () => {
        setIsSearchActive(!isSearchActive);
    };

    const StyledImg = styled('img')(({ theme }) => ({
        height: '46px',
        cursor: 'pointer',
        [theme.breakpoints.down('sm')]: {
            height: '30px'
        }
    }));

    const StyledToolbar = styled(Toolbar)(({ theme }) => ({
        '&.MuiToolbar-root': {
            padding: '18px 0px',
            minHeight: '80px',
            display: 'flex',
            justifyContent: 'space-between',
            [theme.breakpoints.down('md')]: {
                padding: '4px 0px'
            }
        }
    }));

    const StyledTypography = styled(Typography)({
        margin: 'auto'
    });
    const activeStyle = {
        backgroundColor: 'rgba(111, 54, 142, 0.1)',
        fontWeight: '600',
        borderRadius: '60px',
        padding: '0.5rem 1rem',
        color: '#6F368E'
    };

    return (
        <>
            {isLoggingOut && <ScreenLoader />}
            <ElevationScroll>
                <MuiAppBar sx={{ position: 'sticky', pr: '0 !important' }}>
                    <MerLionHeader />
                    <Container>
                        <StyledToolbar data-testid="home-page-toolbar">
                            <Stack direction="row" spacing={3}>
                                <Typography component="div">
                                    <StyledImg src={Logo} onClick={() => navigate('/')} alt="IMDA Logo" />
                                </Typography>
                                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                                    <Box sx={{ height: 'fit-content' }}>
                                        <p className={styles.irisheader}>INTEGRATED REGULATORY INFORMATION SYSTEM (IRIS)</p>
                                    </Box>
                                </Box>
                            </Stack>
                            <Box sx={{ display: 'flex', paddingLeft: '10px', minWidth: 'max-content' }}>
                                {isLoggedIn && (
                                    <>
                                        <Box
                                            sx={{
                                                display: { xs: 'none', md: 'flex', alignItems: 'center' },
                                                '&:hover': {
                                                    cursor: 'pointer'
                                                },
                                                minWidth: 'max-content'
                                            }}
                                            onClick={() => navigate('/my-applications')}
                                        >
                                            <StyledTypography>{user?.role === 'singpass' ? userId : userUen}</StyledTypography>
                                            <Avatar
                                                variant="rounded"
                                                sx={{
                                                    transition: 'all .2s ease-in-out',
                                                    background: 'transparent',
                                                    color: theme.palette.primary.dark
                                                }}
                                                id="profile-button"
                                            >
                                                <AccountCircleIcon />
                                            </Avatar>
                                        </Box>

                                        <Box
                                            sx={{
                                                ml: 2,
                                                mr: 2,
                                                [theme.breakpoints.down('md')]: {
                                                    mr: 2
                                                },
                                                display: 'flex'
                                            }}
                                        >
                                            <NotificationList />
                                        </Box>
                                    </>
                                )}
                                <MobileMenu
                                    id="app-bar-menu"
                                    isUserLogged={isLoggedIn}
                                    handleLogin={handleLogin}
                                    userId={userId}
                                    userUen={userUen}
                                />
                                <IconButton
                                    sx={{ color: '#01131B', mr: '24px', display: { xs: 'none', md: 'flex' } }}
                                    onClick={handleOnclickSearch}
                                    aria-label="search"
                                >
                                    {!isSearchActive ? <SearchIcon /> : <CloseIcon />}
                                </IconButton>
                                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                    <ImdaButton
                                        onClick={handleLogin}
                                        data-testid={isLoggedIn ? 'logout-button' : 'login-button'}
                                        id={isLoggedIn ? 'logout-button' : 'login-button'}
                                        btnClass={isLoggedIn ? 'secondary' : 'primary'}
                                    >
                                        {isLoggedIn ? LOGOUT_TEXT : LOGIN_TEXT}
                                    </ImdaButton>
                                </Box>
                            </Box>
                        </StyledToolbar>
                        <Box sx={{ display: { xs: 'block', md: 'none' }, alignItems: 'center' }}>
                            <Box sx={{ height: 'fit-content' }}>
                                <p className={styles.irisheadermobile}>INTEGRATED REGULATORY INFORMATION SYSTEM (IRIS)</p>
                            </Box>
                        </Box>
                        <Stack
                            direction="row"
                            sx={{
                                display: { xs: 'none', md: 'block' },
                                flexGrow: 1,
                                pr: 3,
                                pb: 1
                            }}
                            spacing={3}
                        >
                            <StyledLink
                                data-testid="app-bar-applications"
                                id="app-bar-applications"
                                to="/applications"
                                style={({ isActive }) => {
                                    if (isActive) {
                                        return activeStyle;
                                    }
                                    if (location.pathname.split('/')[1] === 'application') {
                                        return activeStyle;
                                    }
                                    return undefined;
                                }}
                            >
                                {APP_TEXT}
                            </StyledLink>
                            <StyledLink
                                data-testid="app-bar-guides"
                                id="app-bar-guides"
                                to="/guides"
                                style={({ isActive }) => {
                                    if (isActive) {
                                        return activeStyle;
                                    }
                                    if (location.pathname.split('/')[1] === 'guide') {
                                        return activeStyle;
                                    }
                                    return undefined;
                                }}
                            >
                                {GUIDES_TEXT}
                            </StyledLink>
                            <StyledLink
                                data-testid="app-bar-databases"
                                id="app-bar-databases"
                                to="/databases"
                                style={({ isActive }) => (isActive ? activeStyle : undefined)}
                            >
                                {DB_TEXT}
                            </StyledLink>
                        </Stack>
                        {!isVisible && <Box sx={{ display: { xs: 'block', lg: 'none' }, alignItems: 'center' }}>{Header}</Box>}
                    </Container>
                    {isSearchActive && <SearchBar />}
                </MuiAppBar>
            </ElevationScroll>
        </>
    );
}

export default AppBar;
